import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import type { EncapsulatedTask } from 'ember-concurrency';
import { task, dropTask } from 'ember-concurrency';
import link from 'ember-link/helpers/link';
import RouteTemplate from 'ember-route-template';
import set from 'ember-set-helper/helpers/set';
import DeleteInvitesConfirmation from 'garaje/components/invites/delete-invites-confirmation';
import SendNotificationModal from 'garaje/components/invites/send-notification-modal';
import InvitesSpreadsheetGroup from 'garaje/components/invites/spreadsheet-group';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiButton from 'garaje/components/ui-button';
import type FlowModel from 'garaje/models/flow';
import type GroupInviteModel from 'garaje/models/group-invite';
import type InviteModel from 'garaje/models/invite';
import RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import RecurringRule from 'garaje/models/recurring-rule';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { DATE_FNS_YYYY_MM_DD } from 'garaje/utils/date-fns-tz-utilities';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import { NON_ASSIGNABLE_FLOWS } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type PlainObject from 'garaje/utils/plain-object';
import type { RecordArray } from 'garaje/utils/type-utils';
import { filter, sortBy } from 'macro-decorators';
import { defer } from 'rsvp';
import type { ValueOf } from 'type-fest';

import type { VisitorsInvitesIndexRouteModelParams } from '../../index/route';

import type { VisitorsGroupInvitesShowRouteModel } from './route';

const NON_ASSIGNABLE_FLOW_STRINGS = NON_ASSIGNABLE_FLOWS.map((f): string => `${f}`);

interface VisitorsInvitesGroupShowTemplateSignature {
  Args: {
    model: VisitorsGroupInvitesShowRouteModel;
  };
}

class VisitorsInvitesGroupShowTemplate extends Component<VisitorsInvitesGroupShowTemplateSignature> {
  @service declare abilities: AbilitiesService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare router: RouterService;

  @filter(
    'args.model.flows',
    (flow: FlowModel) => !NON_ASSIGNABLE_FLOW_STRINGS.includes(flow.type) && !flow.employeeCentric,
  )
  flowOptionsInit!: RecordArray<FlowModel>;
  @sortBy('flowOptionsInit', 'position') flowOptions!: FlowModel[];

  @tracked isShowingDeletedRecurringGroupInvite = false;

  // Stub for property support
  get locationIsConnectedToProperty(): boolean {
    // return this.model.connectedTenants?.length > 0;
    return false;
  }

  get canSelectMultipleInvites(): boolean {
    return (
      this.abilities.can('view all invites') ||
      this.abilities.can('edit invites') ||
      this.abilities.can('delete invites')
    );
  }

  get canDelete(): boolean {
    return this.abilities.can('delete invite', this.args.model.groupInvite);
  }

  // Remove related invites from the store to avoid
  // "Passed unknown unsubscribe token to unsubscribe" error
  unloadAssociatedInvites(groupInvite: GroupInviteModel): void {
    const { store } = this;
    const loadedInvites = store
      .peekAll('invite')
      .filter((i: InviteModel) => i.groupInviteId && i.groupInviteId === groupInvite.id);

    loadedInvites.forEach((i: InviteModel) => store.unloadRecord(i));
  }

  @action
  groupInviteDidSave(_date: Date | null, model?: GroupInviteModel | RecurringGroupInvite): void {
    if (model instanceof RecurringGroupInvite) {
      if (model.id !== this.args.model.parentRecurringGroupInvite?.id) {
        // if a new recurring group invite was created, transition the user to the next group invite
        const nextGroupInvite = model.belongsTo('nextGroupInvite').id();
        if (nextGroupInvite) this.router.transitionTo('visitors.invites.groups.show', nextGroupInvite);
      } else {
        // Reload the current group invite to reflect any changes
        this.args.model.groupInvite.reload();
      }
    }
  }

  @action
  onSubmitDeletedRecurringInvite(scope: ValueOf<typeof RECURRING_OPTIONS>): void {
    void this.deleteGroupInviteTask.perform(scope);
  }

  async #deleteGroupInvite(): Promise<void> {
    const { groupInvite } = this.args.model;
    const {
      expectedArrivalTime,
      location: { timezone },
    } = groupInvite;

    let confirmedNotifyCancel: unknown = false;
    let cancellationPayload: unknown = undefined;

    [confirmedNotifyCancel, cancellationPayload] = <[boolean, PlainObject<unknown>]>(
      await this.confirmGroupInviteDeletionTask.perform()
    );

    if (!confirmedNotifyCancel) return;

    try {
      const queryParams: Partial<VisitorsInvitesIndexRouteModelParams> = {};
      queryParams.date = formatInTimeZone(expectedArrivalTime!, timezone, DATE_FNS_YYYY_MM_DD);

      this.unloadAssociatedInvites(groupInvite);

      await groupInvite.destroyRecord();

      // If sending a custom message about cancellation, trigger notify
      if (cancellationPayload) {
        await groupInvite.notifyGroupInvite({ ...cancellationPayload });
      }

      this.flashMessages.showAndHideFlash('success', `Deleted "${groupInvite.groupName}"`);

      this.router.transitionTo('visitors.invites.index', { queryParams });
    } catch (err) {
      const error = parseErrorForDisplay(err) ?? 'Unknown error. Saving failed.';

      this.flashMessages.showFlash('error', error);
    }
  }

  async #deleteRecurringGroupInvite(): Promise<void> {
    const { groupInvite } = this.args.model;
    const parentRecurringGroupInvite = this.args.model.parentRecurringGroupInvite!;
    const parentGroupName = parentRecurringGroupInvite.groupName;
    const {
      expectedArrivalTime,
      location: { timezone },
    } = groupInvite;

    try {
      const queryParams: Partial<VisitorsInvitesIndexRouteModelParams> = {};
      queryParams.date = formatInTimeZone(expectedArrivalTime!, timezone, DATE_FNS_YYYY_MM_DD);

      this.unloadAssociatedInvites(groupInvite);

      const dayBeforeDate = subDays(expectedArrivalTime!, 1);

      const parsedRule = RecurringRule.parse(parentRecurringGroupInvite.recurringRule);
      parsedRule.until = dayBeforeDate;

      parentRecurringGroupInvite.recurringRule = parsedRule.toString();

      await parentRecurringGroupInvite.save();

      this.flashMessages.showAndHideFlash('success', `Deleted "${parentGroupName}"`);

      this.router.transitionTo('visitors.invites.index', { queryParams });
    } catch (err) {
      const error = parseErrorForDisplay(err) ?? 'Unknown error. Saving failed.';
      this.flashMessages.showFlash('error', error);
    }
  }

  deleteGroupInviteTask = task(async (scope?: ValueOf<typeof RECURRING_OPTIONS>): Promise<void> => {
    if (this.args.model.parentRecurringGroupInvite && !scope) {
      this.isShowingDeletedRecurringGroupInvite = true;
      return;
    }

    if (scope === RECURRING_OPTIONS.THIS_AND_FOLLOWING && this.args.model.parentRecurringGroupInvite) {
      // Deleting recurring group invite
      await this.#deleteRecurringGroupInvite();
      return;
    }

    await this.#deleteGroupInvite();
  });

  @dropTask
  confirmGroupInviteDeletionTask = <EncapsulatedTask<unknown, [], { abort: () => void; continue: () => void }>>(<
    unknown
  >{
    *perform(this: {
      context: VisitorsInvitesGroupShowTemplate;
      abort?: () => void;
      continue?: (payload: unknown) => void;
    }) {
      const deferred = defer();

      this.abort = () => deferred.resolve([false]);
      this.continue = (payload?: unknown) => deferred.resolve([true, payload]);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return yield deferred.promise;
    },
  });

  <template>
    <UiPage class="ui__page--noexpand-x">
      <div class="flex">
        <UiHeader @backLink={{link route="visitors.invites"}} class="!mb-2 flex-grow">
          Edit
          {{if @model.parentRecurringGroupInvite "recurring" ""}}
          group invite
        </UiHeader>

        {{#if this.canDelete}}
          <div class="flex-none">
            <UiButton
              type="button"
              class="btn btn--outline"
              disabled={{@model.groupInvite.inProgress}}
              {{on "click" (fn this.deleteGroupInviteTask.perform undefined)}}
              data-test-group-invite-destroy
            >
              <span class="icon-trash"></span>
              <span>Delete group</span>
            </UiButton>
          </div>
        {{/if}}
      </div>

      <InvitesSpreadsheetGroup
        @model={{@model.groupInvite}}
        @changeset={{@model.changeset}}
        @flows={{this.flowOptions}}
        @handsontable={{@model.handsontable}}
        @isEditInvite={{true}}
        @locationIsConnectedToProperty={{this.locationIsConnectedToProperty}}
        @enableInviteSelection={{this.canSelectMultipleInvites}}
        @onSave={{this.groupInviteDidSave}}
        @shouldShowRecurrence={{if @model.parentRecurringGroupInvite true}}
      />
    </UiPage>

    {{#if this.confirmGroupInviteDeletionTask.isRunning}}
      {{#if this.confirmGroupInviteDeletionTask.last}}
        <SendNotificationModal
          @callback={{this.confirmGroupInviteDeletionTask.last.continue}}
          @onClickBack={{this.confirmGroupInviteDeletionTask.last.abort}}
          @action="delete"
          @invites={{array null}}
          @backText="Cancel"
          @prompt="Send a cancellation email to all the visitors in the group?"
        />
      {{/if}}
    {{/if}}

    {{#if this.isShowingDeletedRecurringGroupInvite}}
      <DeleteInvitesConfirmation
        @onSubmit={{this.onSubmitDeletedRecurringInvite}}
        @modelName="group invite"
        @hasRecurringInvite={{true}}
        @close={{set this "isShowingDeletedRecurringGroupInvite" false}}
      />
    {{/if}}
  </template>
}

export default RouteTemplate(VisitorsInvitesGroupShowTemplate);
